import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api, { contentAPI } from '../../services/api';
import SelectInput from '../../components/form/SelectInput';
import TextInput from '../../components/form/TextInput';
import TextArea from '../../components/form/TextArea';
import { formatJson } from '../../components/form/JsonTextArea';
import StructuredJsonEditor from '../../components/form/StructuredJsonEditor';
import PhoneInput from '../../components/form/PhoneInput';
import CheckboxInput from '../../components/form/CheckboxInput';
import { LocationPicker } from '../../components/maps/LocationMap';
import { safeJsonParse } from '../../utils/storage';
import '../../components/form/FormStyles.css';
import './AdminStaffMemberForm.css';

const facilityTypeOptions = [
  { value: 'hospital', label: '🏥 Hospital (अस्पताल)' },
  { value: 'health_center', label: '⚕️ Health Center (आरोग्य केंद्र)' },
  { value: 'ambulance', label: '🚑 Ambulance Service (रुग्णवाहिका सेवा)' },
  { value: 'emergency_service', label: '🆘 Emergency Service (आपत्कालीन सेवा)' },
  { value: 'tourism', label: '✈️ Tourism (पर्यटन)' },
  { value: 'religious_site', label: '🧘 Religious Site (धार्मिक स्थळ)' },
  { value: 'educational', label: '📚 Educational (शैक्षणिक)' },
  { value: 'water_supply', label: '💧 Water Supply (जल पुरवठा)' },
  { value: 'electricity', label: '⚡ Electricity (विद्युत)' },
  { value: 'fire_station', label: '🚒 Fire Station (अग्निशमन केंद्र)' },
  { value: 'police', label: '👮 Police Station (पोलिस स्टेशन)' },
  { value: 'veterinary', label: '🐾 Veterinary (पशुचिकित्सा)' },
  { value: 'other', label: '📌 Other (इतर)' },
];

const availabilityOptions = [
  { value: '24/7', label: '24/7 Available' },
  { value: 'business_hours', label: 'Business Hours (कार्य सुचक कालावधी)' },
  { value: 'seasonal', label: 'Seasonal (ऋतुपरिवर्तनीय)' },
  { value: 'on_demand', label: 'On Demand (मागणीनुसार)' },
];

const apiErrorMessage = (error, fallback) => {
  const details = error.response?.data?.details;
  if (Array.isArray(details) && details.length) {
    return details.map(item => `${item.field || 'field'}: ${item.message}`).join(', ');
  }
  return error.response?.data?.error || fallback;
};

const normalizeFacilityPayload = (data) => {
  const payload = {
    ...data,
    additional_details: typeof data.additional_details === 'object'
      ? data.additional_details
      : safeJsonParse(data.additional_details),
  };

  ['latitude', 'longitude'].forEach((field) => {
    if (payload[field] === '') {
      payload[field] = null;
    } else if (payload[field] !== null && payload[field] !== undefined) {
      const parsed = Number(payload[field]);
      payload[field] = Number.isFinite(parsed) ? parsed : null;
    }
  });

  if (payload.availability === '') payload.availability = null;

  return payload;
};

export default function AdminFacilityForm() {
  const { tenantSlug, facilityId } = useParams();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: '',
    facility_type: 'hospital',
    description: '',
    address: '',
    phone: '',
    email: '',
    opening_hours: '',
    availability: 'business_hours',
    latitude: '',
    longitude: '',
    image_url: '',
    in_charge_name: '',
    in_charge_phone: '',
    additional_details: '',
    is_active: true,
  });

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(facilityId ? true : false);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [imageUploading, setImageUploading] = useState(false);
  const imageInputRef = useRef(null);
  const steps = [
    { title: 'मूलभूत माहिती', subtitle: 'Basic details' },
    { title: 'स्थान व संपर्क', subtitle: 'Location & contact' },
    { title: 'परिचालन', subtitle: 'Operating details' },
    { title: 'जबाबदार व्यक्ती', subtitle: 'In-charge person' },
    { title: 'पुनरावलोकन', subtitle: 'Additional & status' },
  ];

  // Fetch existing facility if editing
  useEffect(() => {
    if (facilityId) {
      const fetchFacility = async () => {
        try {
          const response = await api.get(`/tenant/${tenantSlug}/facilities/${facilityId}`);
          setFormData({
            ...response.data,
            additional_details: (() => {
              const details = response.data.additional_details;
              if (!details) return '';
              if (typeof details === 'object') return JSON.stringify(details, null, 2);
              try { return formatJson(details); } catch { return details; }
            })(),
            is_active: response.data.is_active !== false,
          });
        } catch (error) {
          setErrors({ submit: 'Failed to load facility' });
        } finally {
          setLoading(false);
        }
      };
      fetchFacility();
    }
  }, [facilityId, tenantSlug]);

  const validateForm = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = 'Facility name is required';
    if (!formData.facility_type) newErrors.facility_type = 'Facility type is required';
    if (!formData.description.trim()) newErrors.description = 'Description is required';
    if (formData.phone && formData.phone.replace(/\D/g, '').length !== 10) {
      newErrors.phone = 'Phone must be 10 digits';
    }
    if (formData.in_charge_phone && formData.in_charge_phone.replace(/\D/g, '').length !== 10) {
      newErrors.in_charge_phone = 'Phone must be 10 digits';
    }
    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Invalid email format';
    }
    if (formData.additional_details) {
      try {
        JSON.parse(formData.additional_details);
      } catch {
        newErrors.additional_details = 'Additional details must be valid JSON';
      }
    }
    if (formData.latitude && (isNaN(formData.latitude) || formData.latitude < -90 || formData.latitude > 90)) {
      newErrors.latitude = 'Latitude must be between -90 and 90';
    }
    if (formData.longitude && (isNaN(formData.longitude) || formData.longitude < -180 || formData.longitude > 180)) {
      newErrors.longitude = 'Longitude must be between -180 and 180';
    }
    return newErrors;
  };

  const validateImageFile = (file) => {
    if (!file) return 'कृपया प्रतिमा निवडा.';
    if (!file.type.startsWith('image/')) return 'केवळ प्रतिमा फायली स्वीकारल्या जातात.';
    if (file.size > 5 * 1024 * 1024) return 'प्रतिमा 5MB पेक्षा लहान असावी.';
    return null;
  };

  const uploadImageFile = async (file) => {
    const validationError = validateImageFile(file);
    if (validationError) throw new Error(validationError);
    const formDataObj = new FormData();
    formDataObj.append('image', file);
    const response = await contentAPI.uploadImage(tenantSlug, formDataObj);
    return response.data?.url;
  };

  const handleFacilityImageFile = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    event.target.value = '';
    setImageUploading(true);
    try {
      const url = await uploadImageFile(file);
      if (url) setFormData(prev => ({ ...prev, image_url: url }));
    } catch (error) {
      setErrors(prev => ({ ...prev, submit: error.message || 'Image upload failed.' }));
    } finally {
      setImageUploading(false);
    }
  };

  const beginFacilityImageUpload = () => {
    imageInputRef.current?.click();
  };

  const stepFields = [
    ['name', 'facility_type', 'description'],
    ['phone', 'email', 'latitude', 'longitude'],
    [],
    ['in_charge_phone'],
    ['additional_details'],
  ];

  const validateStep = (stepIndex) => {
    const allErrors = validateForm();
    return stepFields[stepIndex].reduce((result, field) => {
      if (allErrors[field]) result[field] = allErrors[field];
      return result;
    }, {});
  };

  const moveStep = (nextStep) => {
    if (nextStep > currentStep) {
      const stepErrors = validateStep(currentStep);
      if (Object.keys(stepErrors).length) {
        setErrors(current => ({ ...current, ...stepErrors }));
        return;
      }
    }
    setCurrentStep(Math.max(0, Math.min(nextStep, steps.length - 1)));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
    // Clear error for this field
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: '',
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const submitter = e.nativeEvent?.submitter;
    if (submitter?.dataset?.facilitySubmit !== 'true') {
      return;
    }

    const newErrors = validateForm();
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      const invalidStep = steps.findIndex((_, index) => Object.keys(validateStep(index)).length);
      if (invalidStep >= 0) setCurrentStep(invalidStep);
      return;
    }

    try {
      setSubmitting(true);
      const method = facilityId ? 'put' : 'post';
      const endpoint = facilityId
        ? `/tenant/${tenantSlug}/facilities/${facilityId}`
        : `/tenant/${tenantSlug}/facilities`;

      await api[method](endpoint, normalizeFacilityPayload(formData));
      setSuccess(true);
      setTimeout(() => {
        navigate(`/admin/${tenantSlug}/facilities`);
      }, 1500);
    } catch (error) {
      setErrors({ submit: apiErrorMessage(error, 'Failed to save facility') });
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return <div className="form-container staff-member-form facility-wizard-form">Loading...</div>;
  }

  return (
    <div className="form-container staff-member-form facility-wizard-form">
      <h1>{facilityId ? 'Edit Facility' : 'Add New Facility'}</h1>

      {success && <div className="success-message">Facility saved successfully!</div>}
      {errors.submit && <div style={{ color: '#e74c3c', marginBottom: '20px' }}>{errors.submit}</div>}

      <form onSubmit={handleSubmit}>
        <nav className="staff-form-stepper" aria-label="Facility form steps">
          {steps.map((step, index) => (
            <button
              type="button"
              key={step.title}
              className={`${index === currentStep ? 'active' : ''} ${index < currentStep ? 'completed' : ''}`}
              disabled={index > currentStep}
              onClick={() => moveStep(index)}
              aria-current={index === currentStep ? 'step' : undefined}
            >
              <span>{index < currentStep ? '✓' : index + 1}</span>
              <div><strong>{step.title}</strong><small>{step.subtitle}</small></div>
            </button>
          ))}
        </nav>
        <div className="form-inner">
        {/* Basic Information */}
        {currentStep === 0 && <div className="form-section staff-step-panel">
          <h3>बेसिक माहिती (Basic Information)</h3>

          <TextInput
            label="Facility Name (सुविधाचे नाव) *"
            name="name"
            value={formData.name}
            onChange={handleChange}
            error={errors.name}
            placeholder="e.g., Primary Health Center"
            required
          />

          <SelectInput
            label="Facility Type (सुविधेचा प्रकार) *"
            name="facility_type"
            value={formData.facility_type}
            onChange={handleChange}
            options={facilityTypeOptions}
            error={errors.facility_type}
            required
          />

          <TextArea
            label="Description (वर्णन) *"
            name="description"
            value={formData.description}
            onChange={handleChange}
            error={errors.description}
            rows={4}
            required
          />

          <div style={{ display: 'grid', gap: 8 }}>
            <label className="registry-field" htmlFor="image_url">
              <span>Image URL (फोटो URL)</span>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <input
                  id="image_url"
                  name="image_url"
                  value={formData.image_url}
                  onChange={handleChange}
                  placeholder="https://example.com/facility-photo.jpg"
                  style={{ flex: 1 }}
                />
                <button
                  type="button"
                  className="btn btn-outline btn-sm"
                  onClick={beginFacilityImageUpload}
                  disabled={imageUploading}
                >
                  {imageUploading ? 'Uploading...' : 'Upload'}
                </button>
              </div>
            </label>
            <input
              ref={imageInputRef}
              type="file"
              accept="image/*"
              style={{ display: 'none' }}
              onChange={handleFacilityImageFile}
            />
          </div>
        </div>}

        {/* Location & Contact */}
        {currentStep === 1 && <div className="form-section staff-step-panel facility-location-step">
          <h3>स्थान व संपर्क (Location & Contact)</h3>

          <TextArea
            label="Address (पता)"
            name="address"
            value={formData.address}
            onChange={handleChange}
            rows={3}
          />

          <div className="form-row">
            <PhoneInput
              label="Phone (फोन)"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              error={errors.phone}
            />

            <TextInput
              label="Email (ईमेल)"
              name="email"
              type="email"
              value={formData.email}
              onChange={handleChange}
              error={errors.email}
            />
          </div>

          <LocationPicker
            id="facility-location"
            latitude={formData.latitude}
            longitude={formData.longitude}
            onChange={coordinates => setFormData(current => ({ ...current, ...coordinates }))}
            label="सुविधेचे स्थान / Facility location"
          />
          {(errors.latitude || errors.longitude) && <p className="form-error">{errors.latitude || errors.longitude}</p>}
        </div>}

        {/* Operating Information */}
        {currentStep === 2 && <div className="form-section staff-step-panel">
          <h3>परिचालन माहिती (Operating Information)</h3>

          <TextInput
            label="Opening Hours (खुल्या राहण्याचे वेळ)"
            name="opening_hours"
            value={formData.opening_hours}
            onChange={handleChange}
            placeholder="e.g., 9:00 AM - 5:00 PM (Mon-Fri)"
          />

          <SelectInput
            label="Availability (उपलब्धता)"
            name="availability"
            value={formData.availability}
            onChange={handleChange}
            options={availabilityOptions}
          />
        </div>}

        {/* In-Charge Information */}
        {currentStep === 3 && <div className="form-section staff-step-panel">
          <h3>जबाबदार व्यक्ती (In-Charge Person)</h3>

          <TextInput
            label="In-Charge Name (जबाबदार नाव)"
            name="in_charge_name"
            value={formData.in_charge_name}
            onChange={handleChange}
          />

          <PhoneInput
            label="In-Charge Phone (जबाबदार फोन)"
            name="in_charge_phone"
            value={formData.in_charge_phone}
            onChange={handleChange}
            error={errors.in_charge_phone}
          />
        </div>}

        {currentStep === 4 && <div className="form-section staff-step-panel">
          <h3>अतिरिक्त तपशील (Additional Details)</h3>
          <StructuredJsonEditor
            label="Additional features"
            name="additional_details"
            value={formData.additional_details}
            onChange={handleChange}
            error={errors.additional_details}
            placeholder='{"doctors":2,"nurses":3,"beds":10,"services":["General consultation","Vaccination"]}'
          />

        {/* Status */}
          <CheckboxInput
            label="Active (सक्रिय)"
            name="is_active"
            checked={formData.is_active}
            onChange={handleChange}
            className="facility-status-toggle"
          />
          <div className="staff-review-grid">
            <div><span>Facility</span><strong>{formData.name || '—'}</strong></div>
            <div><span>Facility type</span><strong>{facilityTypeOptions.find(item => item.value === formData.facility_type)?.label || '—'}</strong></div>
            <div><span>Contact phone</span><strong>{formData.phone || 'Not provided'}</strong></div>
            <div><span>Availability</span><strong>{availabilityOptions.find(item => item.value === formData.availability)?.label || 'Not configured'}</strong></div>
            <div><span>Location selected</span><strong>{formData.latitude && formData.longitude ? 'Yes' : 'No'}</strong></div>
            <div><span>Status</span><strong>{formData.is_active ? 'Active' : 'Inactive'}</strong></div>
          </div>
        </div>}

        {/* Actions */}
        </div>
        <div className="form-actions staff-step-actions">
          <button
            type="button"
            className="btn-secondary"
            onClick={() => navigate(`/admin/${tenantSlug}/facilities`)}
          >
            Cancel
          </button>
          <div>
            {currentStep > 0 && <button type="button" className="btn-secondary" onClick={() => moveStep(currentStep - 1)}>← Previous</button>}
            {currentStep < steps.length - 1 ? (
              <button key="facility-next" type="button" className="btn-primary" onClick={() => moveStep(currentStep + 1)}>Next →</button>
            ) : (
              <button
                key="facility-submit"
                type="submit"
                data-facility-submit="true"
                className="btn-primary"
                disabled={submitting}
              >
                {submitting ? 'Saving...' : facilityId ? 'Update Facility' : 'Add Facility'}
              </button>
            )}
          </div>
        </div>
      </form>
    </div>
  );
}
